// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Globe, RotateCcw } from "lucide-react";
import { useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  type TaskCard,
  type TaskCardScope,
  useSaveTaskCardMutation,
  useTaskCardQuery,
  useTaskQuery,
} from "@/api/hooks/use-tasks";
import { FilterSelect } from "@/components/shared/filter-select";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RevertTaskOverrideDialog, type RevertTaskOverrideTarget } from "@/features/tasks/revert-task-override-dialog";
import { TaskCardSectionEditor } from "@/features/tasks/task-card-editors";
import { TaskCardPreview, type TaskCardSection } from "@/features/tasks/task-card-preview/task-card-preview";
import type { TaskCardSettings } from "@/features/tasks/types";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { cn } from "@/lib/utils";

/** The regions offered in the section rail, in the order the card lays them out. */
const SECTIONS: TaskCardSection[] = [
  "identity",
  "header",
  "subHeader",
  "topContent",
  "tabs",
  "infoFields",
  "actions",
  "snooze",
  "toggles",
];

export interface TaskEditTabHandle {
  reset: () => void;
}

interface TaskEditTabProps {
  subtype: string;
  canEdit: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<TaskEditTabHandle>;
}

/**
 * The visual card editor.
 *
 * The card preview on the left is the canvas: clicking a region's pencil opens that region's
 * editor on the right and the preview repaints as values change, so an author sees the handheld
 * result before saving. The scope picker decides where the edit lands — the global card, or a
 * chain/store override that stores only what departs from it.
 */
export function TaskEditTab({ subtype, canEdit, onDirtyChange, ref }: TaskEditTabProps) {
  const { t } = useTranslation("back-office");
  const { data: context, isLoading: contextLoading } = useBackofficeContext();

  const [chainName, setChainName] = useState("");
  const [storeName, setStoreName] = useState("");
  const scope = useMemo<TaskCardScope>(
    () => ({ chainName: chainName || undefined, storeName: storeName || undefined }),
    [chainName, storeName],
  );

  const { data: card, isLoading } = useTaskCardQuery(subtype, scope);
  // The detail query is already cached by the page around this tab, so this costs no request. It
  // carries every chain/store that already overrides the card, which the scope picker marks.
  const { data: task } = useTaskQuery(subtype);
  const overriddenChains = useMemo(
    () => new Set((task?.scopes ?? []).filter((s) => s.chainName && !s.storeName).map((s) => s.chainName)),
    [task?.scopes],
  );
  const overriddenStores = useMemo(
    () => new Set((task?.scopes ?? []).filter((s) => s.storeName).map((s) => s.storeName)),
    [task?.scopes],
  );

  if (isLoading || !card) {
    return <InlineSpinner label={t("tasks.editor.loading")} />;
  }

  return (
    <TaskCardEditor
      // Re-seed the draft whenever the scope changes — each scope is a different card.
      key={`${subtype}:${chainName}:${storeName}`}
      card={card}
      subtype={subtype}
      canEdit={canEdit}
      onDirtyChange={onDirtyChange}
      ref={ref}
      chainName={chainName}
      storeName={storeName}
      onScopeChange={(nextChain, nextStore) => {
        setChainName(nextChain);
        setStoreName(nextStore);
      }}
      chains={(context?.chains ?? []).map((chain) => ({
        label: chain.display,
        value: chain.value,
        marked: overriddenChains.has(chain.value),
      }))}
      stores={(context?.stores ?? []).map((store) => ({
        label: store.display,
        value: store.value,
        marked: overriddenStores.has(store.value),
      }))}
      contextLoading={contextLoading}
    />
  );
}

interface TaskCardEditorProps {
  card: TaskCard;
  subtype: string;
  canEdit: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<TaskEditTabHandle>;
  chainName: string;
  storeName: string;
  onScopeChange: (chainName: string, storeName: string) => void;
  chains: { label: string; value: string; marked?: boolean }[];
  stores: { label: string; value: string; marked?: boolean }[];
  contextLoading: boolean;
}

function TaskCardEditor({
  card,
  subtype,
  canEdit,
  onDirtyChange,
  ref,
  chainName,
  storeName,
  onScopeChange,
  chains,
  stores,
  contextLoading,
}: TaskCardEditorProps) {
  const { t } = useTranslation("back-office");
  const saveCard = useSaveTaskCardMutation(subtype);
  const [revertTarget, setRevertTarget] = useState<RevertTaskOverrideTarget | null>(null);

  const [draft, setDraft] = useState<TaskCardSettings>(card.settings);
  const [section, setSection] = useState<TaskCardSection>("header");

  const isGlobalScope = card.scope === "global";
  const hasOverride = card.hasDefinitionOverride || card.hasAppearanceOverride;
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(card.settings), [draft, card.settings]);

  useEffect(
    function reportDirtyState() {
      onDirtyChange?.(dirty);
      return () => onDirtyChange?.(false);
    },
    [dirty, onDirtyChange],
  );

  useImperativeHandle(ref, () => ({ reset: () => setDraft(card.settings) }), [card.settings]);

  function applyPatch(patch: Partial<TaskCardSettings>) {
    setDraft((previous) => ({ ...previous, ...patch }));
  }

  async function handleSave(event: React.FormEvent) {
    event.preventDefault();
    try {
      await saveCard.mutateAsync({
        scope: { chainName: chainName || undefined, storeName: storeName || undefined },
        settings: draft,
      });
      toast.success(t("tasks.editor.saved"));
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <form onSubmit={handleSave} className="space-y-6 p-6">
      {/* The scope row sits 16px above the canvas, matching a data table's toolbar-to-table gap;
          the form's own 24px rhythm still separates this block from the save pane. */}
      <div className="space-y-4">
        {/* Scope picker — decides whether an edit lands globally or as an override. Laid out like a
          data-table toolbar: controls left, trailing action right, no surrounding panel. */}
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <Button
              type="button"
              variant={isGlobalScope ? "default" : "outline"}
              onClick={() => onScopeChange("", "")}
              disabled={dirty}
            >
              <Globe className="h-4 w-4" />
              {t("tasks.scope.global")}
            </Button>
            <FilterSelect
              label={t("tasks.editor.scopeChain")}
              value={chainName}
              onChange={(value) => onScopeChange(value, "")}
              options={chains}
              searchPlaceholder={t("stores.form.searchChains")}
              emptyText={t("stores.form.noChains")}
              loading={contextLoading}
              disabled={dirty}
              markTitle={t("tasks.editor.scopeHasOverride")}
            />
            <FilterSelect
              label={t("tasks.editor.scopeStore")}
              value={storeName}
              onChange={(value) => onScopeChange("", value)}
              options={stores}
              searchPlaceholder={t("users.list.filter.searchStores")}
              emptyText={t("users.list.filter.noStores")}
              loading={contextLoading}
              disabled={dirty}
              markTitle={t("tasks.editor.scopeHasOverride")}
            />
          </div>
          {!isGlobalScope && hasOverride && (
            <div className="flex shrink-0 items-center gap-2 self-start">
              <Button
                type="button"
                variant="outline"
                disabled={dirty}
                onClick={() =>
                  setRevertTarget({
                    subtype,
                    scopeLabel: card.storeName ?? card.chainName ?? "",
                    chainName: chainName || undefined,
                    storeName: storeName || undefined,
                  })
                }
              >
                <RotateCcw className="h-4 w-4" />
                {t("tasks.editor.revertOverride")}
              </Button>
            </div>
          )}
        </div>

        {/* Always two columns: the card on the left, the editor for the selected region on the
          right. Stacking them would put the editor below the fold and break the see-as-you-edit
          loop the canvas exists for. */}
        <div className="flex flex-row gap-6">
          {/* Canvas */}
          <div className="shrink-0">
            <TaskCardPreview
              settings={draft}
              chainName={chainName || undefined}
              activeSection={section}
              onSelectSection={setSection}
            />
          </div>

          {/* Section rail + editor panel */}
          <div className="min-w-0 flex-1 space-y-4">
            <div className="flex flex-wrap gap-1.5">
              {SECTIONS.map((candidate) => (
                <Button
                  key={candidate}
                  type="button"
                  size="sm"
                  variant={candidate === section ? "default" : "outline"}
                  onClick={() => setSection(candidate)}
                  className={cn("h-7 text-xs")}
                >
                  {t(`tasks.sections.${candidate}`)}
                </Button>
              ))}
            </div>

            <div className="rounded-lg border p-4">
              <div className="mb-4 flex items-center gap-2">
                <h3 className="text-sm font-medium">{t(`tasks.sections.${section}`)}</h3>
                {!isGlobalScope && (
                  <Badge variant="outline" className="font-normal">
                    {card.storeName ?? card.chainName}
                  </Badge>
                )}
              </div>
              <TaskCardSectionEditor
                section={section}
                settings={draft}
                onChange={applyPatch}
                disabled={!canEdit}
                overriddenFields={card.overriddenFields}
                isGlobalScope={isGlobalScope}
              />
            </div>
          </div>
        </div>
      </div>

      <RevertTaskOverrideDialog target={revertTarget} onClose={() => setRevertTarget(null)} />

      {dirty && (
        <SaveDiscardPane
          onDiscard={() => setDraft(card.settings)}
          saving={saveCard.isPending}
          saveDisabled={!canEdit}
        />
      )}
    </form>
  );
}
